import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  forwardRef,
  Input,
} from "@angular/core";
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormGroup,
  FormBuilder,
  Validators,
  AbstractControl,
  FormArray,
} from "@angular/forms";
import { BehaviorSubject, Observable } from "rxjs";
import { ResizedEvent } from "angular-resize-event";
import { FilterCompleteService } from "../../../../filter-complete/filter-complete";
import { Entity, Plan } from "../../../../model/entity";
import { AliquotTax } from "../../../../model/aliquot-tax";
import { ValidateAutoCompleteSelect } from "../../../../validators/autocomplete-select.validator";
import { FilterService } from "../../../../services/filter.service";
import {
  ValorizationAmount,
  Valorization,
} from "../../../../model/valorization";
import {
  ValorizationItemAmount,
  ValorizationItemSupplie,
} from "../../../../model/valorization-item";
import { ValorizationService } from "../../../../services/valorization/valorization.service";
import { PracticeRegionSearchReservation } from "../../../../model/practice-region-reservation";
import { NotificationToastService } from "../../../../services/notification-toast.service";
import { MatDialog } from "@angular/material";
// tslint:disable-next-line:max-line-length
import { ValorizationItemAmountEditDialogComponent } from "../../../dialogs/valorization/valorization-item-amount-edit/valorization-item-amount-edit-dialog.component";
import { AuthService } from "../../../../services/auth.service";
import { ValorizationType } from "../../../../enums/valorization-type.enum";
import { map, startWith } from "rxjs/operators";
import { CacheFilterService } from "src/app/services/cache/cache-filter.service";
import { PracticeFilter } from "../../../../model/practice/practice-filter";
import { MedicalAppointmentProcedure } from "../../../../model/medical-appointment-procedure";
import { ValorizationItemAddSetInsumoDialogComponent } from "src/app/components/dialogs/valorization/valorization-item-add-set-insumo/valorization-item-add-set-insumo-dialog.component";

export const VALORIZATION_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => ValorizationAmountControlComponent),
  multi: true,
};

@Component({
  selector: "app-valorization-amount-control",
  providers: [VALORIZATION_CONTROL_VALUE_ACCESSOR],
  templateUrl: "./valorization-amount-control.component.html",
  styleUrls: ["./valorization-amount-control.component.css"],
})
export class ValorizationAmountControlComponent
  implements OnInit, ControlValueAccessor
{
  onChange;
  @Input() isRegistered: boolean;
  @Input() editableAgreementPriceIds: number[] = [];
  @Input() isDisabled: boolean;
  @Input() loading: boolean;
  form: FormGroup;
  isAdmission: boolean;
  valorizationId: number | null = null;

  aliquotsTax: AliquotTax[] = [];

  // tslint:disable-next-line:no-output-on-prefix
  @Output() onRecalculateClick = new EventEmitter();
  // tslint:disable-next-line:no-output-on-prefix
  @Output() onAddPracticeClick = new EventEmitter();

  @Output() onAddSetInsumoClick = new EventEmitter();

  // tslint:disable-next-line:no-output-on-prefix
  @Output() onRefreshPractice = new EventEmitter();
  @Output() onRefreshPracticeToParticular = new EventEmitter();
  widthTableExp = 800;
  filterCompleteEntities: FilterCompleteService<Entity>;
  filterCompletePlans: FilterCompleteService<Plan>;
  filterCompleteAliquotsTax: FilterCompleteService<AliquotTax>;
  filterCompletePosiblePractices: Observable<PracticeRegionSearchReservation[]>;
  posiblePractices: PracticeRegionSearchReservation[] = [];
  practices: PracticeFilter[] = [];

  displayAllValorizationItemsColumns = [
    "practiceName",
    "quantity",
    "feeAmount",
    "feeCoverage",
    "rightsAmount",
    "rightsCoverage",
    "moduleAmount",
    "moduleCoverage",
    "supplieAmount",
    "supplieCoverage",
    "assuranceAmount",
    "totalAmount",
    "patientAmountWithTax",
    "entityAmountWithTax",
    "star",
  ];

  displayNoAmountValorizationItemsColumns = [
    "practiceName",
    "quantity",
    "feeCoverage",
    "rightsCoverage",
    "moduleCoverage",
    "supplieCoverage",
    "star",
  ];

  valorizationItemsDataSource = new BehaviorSubject<AbstractControl[]>([]);

  constructor(
    private formBuilder: FormBuilder,
    private dialog: MatDialog,
    private valorizationService: ValorizationService,
    private notificationToastService: NotificationToastService,
    private authService: AuthService,
    private filterService: FilterService,
    private cacheFilterService: CacheFilterService,
  ) {
    this.form = this.formBuilder.group({
      Entity: [
        { value: "", disabled: this.isDisabled || this.isRegistered },
        [Validators.required, ValidateAutoCompleteSelect],
      ],
      Plan: [
        { value: "", disabled: this.isDisabled || this.isRegistered },
        [Validators.required, ValidateAutoCompleteSelect],
      ],
      PracticeRegionAdd: [""],
      PracticeRegionQuantityAdd: [""],
      PatientAmount: [""],
      PatientAmountByQuantity: [""],
      EntityAmount: [""],
      TotalAmount: [""],
      PatientTaxAmount: [""],
      EntityTaxAmount: [""],
      TotalTaxAmount: [""],
      Valorization: this.formBuilder.group({
        RightsBonus: [""],
        FeeBonus: [""],
        SupplieBonus: [""],
        ModuleBonus: [""],
        Id: [""],
        AssuranceAmount: [""],
        ValorizationTypeId: ["", [Validators.required]],
        PlanId: ["", [Validators.required]],
        MedicalAppointmentId: [""],
        AliquotTax: [
          { value: "", disabled: this.isDisabled || this.isRegistered },
          [Validators.required, ValidateAutoCompleteSelect],
        ],
        CreateDate: [""],
        CreateUser: [""],
        UpdateDate: [""],
        UpdateUser: [""],
      }),
      ValorizationItemsAmount: this.formBuilder.array([]),
    });

    this.filterCompleteEntities = new FilterCompleteService<Entity>(
      [],
      this.form.controls.Entity,
    );
    this.filterCompletePlans = new FilterCompleteService<Plan>(
      [],
      this.form.controls.Plan,
    );
    this.filterCompleteAliquotsTax = new FilterCompleteService<AliquotTax>(
      [],
      this.form.controls.AliquotTax,
    );

    this.filterCompletePosiblePractices =
      this.form.controls.PracticeRegionAdd.valueChanges.pipe(
        startWith<string | PracticeRegionSearchReservation>(""),
        map((value) =>
          typeof value === "string" ? value : value ? value.Name : "",
        ),
        map((name) => this.filterPosiblePractices(name)),
      );
  }

  editEntityInfo() {
    // enable entity and plan and AliquotTax
    if (!this.isDisabled && this.isRegistered && this.valorizationId) {
      this.form.get("Entity").enable();
      this.form.get("Plan").enable();
      this.valorizationFormGroup.get("AliquotTax").enable();
      this.isRegistered = false;
      this.valorizationService.editValidateValorization(this.valorizationId);
    }
  }

  get seeAmounts() {
    return (
      this.authService.seeAdmissionValorizationAmount() || !this.isAdmission
    );
  }
  get valorizationFormGroup() {
    return <FormGroup>this.form.get("Valorization");
  }
  get valorizationItemsAmount() {
    return <FormArray>this.form.get("ValorizationItemsAmount");
  }
  get filteredAliquotsTaxOptions() {
    return this.filterCompleteAliquotsTax
      ? this.filterCompleteAliquotsTax.filteredOptions
      : [];
  }
  get filteredPlansOptions() {
    return this.filterCompletePlans
      ? this.filterCompletePlans.filteredOptions
      : [];
  }
  get filteredEntitiesOptions() {
    return this.filterCompleteEntities
      ? this.filterCompleteEntities.filteredOptions
      : [];
  }
  get f() {
    return this.form.controls;
  }
  get fValorization() {
    return this.valorizationFormGroup.controls;
  }

  filterPosiblePractices(
    seachText?: string,
  ): PracticeRegionSearchReservation[] {
    return seachText
      ? this.posiblePractices.filter(
          (option) =>
            option && this.coincideFilter(option, seachText.split(" ")),
        )
      : this.posiblePractices
        ? this.posiblePractices.slice()
        : [];
  }

  coincideFilter(
    practiceRegionSearchReservation: PracticeRegionSearchReservation,
    filters: string[],
  ): boolean {
    let response = true;
    filters.forEach((filter) => {
      if (
        filters.length === 1 &&
        practiceRegionSearchReservation.Code &&
        practiceRegionSearchReservation.Code === filter
      ) {
        response = true;
      } else if (
        response &&
        practiceRegionSearchReservation.SeachText.toLowerCase().indexOf(
          filter.toLowerCase(),
        ) < 0
      ) {
        response = false;
      }
    });

    return response;
  }

  public displayPosiblePracticesFn(
    item?: PracticeRegionSearchReservation,
  ): string | undefined {
    return item ? item.Name : undefined;
  }

  async writeValue(obj: ValorizationAmount) {
    if (obj) {
      this.valorizationId = obj.Valorization.Id;
      this.form.patchValue({
        Entity: obj.Entity,
        Plan: obj.Plan,
        PracticeRegionAdd: "",
        PracticeRegionQuantityAdd: "",
        PatientAmount: obj.PatientAmount,
        PatientAmountByQuantity: obj.PatientAmountByQuantity,
        EntityAmount: obj.EntityAmount,
        TotalAmount: obj.TotalAmount,
        PatientTaxAmount: obj.PatientTaxAmount,
        EntityTaxAmount: obj.EntityTaxAmount,
        TotalTaxAmount: obj.TotalTaxAmount,
      });

      if (this.isDisabled || this.isRegistered) {
        this.form.get("Entity").disable();
        this.form.get("Plan").disable();
      }

      this.addValorization(obj.Valorization);

      if (obj && obj.Entity) {
        this.filterService.getPlanFilter(obj.Entity.Id).then((plans) => {
          this.filterCompletePlans = new FilterCompleteService<Plan>(
            plans,
            this.form.controls.Plan,
          );
          if (!this.form.controls.Plan.value && plans.length === 1) {
            this.form.patchValue({ Plan: plans[0] });
            this.valorizationFormGroup.patchValue({ PlanId: plans[0].Id });
          }
        });
      }

      if (this.form.getRawValue().Plan) {
        this.filterService
          .getAliquotsTaxByPlanId(this.form.getRawValue().Plan.Id)
          .then((aliquotsTax) => {
            this.filterCompleteAliquotsTax =
              new FilterCompleteService<AliquotTax>(
                aliquotsTax,
                this.valorizationFormGroup.controls.AliquotTax,
              );
          });
      }

      this.addValorizationItemsAmount(obj.ValorizationItemsAmount);
      this.loadPosiblePractices();
    }
  }

  changeValorizacion() {
    const valorizationAmount = this.form.getRawValue();
    if (this.onChange) {
      this.onChange(valorizationAmount);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {}

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  onTableResized(event: ResizedEvent): void {
    this.widthTableExp = event.newWidth - 40;
  }

  onAliquotTaxChange() {
    this.aliquotTaxSelected(null);
  }

  onEntityChange() {
    this.form.patchValue({ Plan: null });
    this.changeValorizacion();
  }

  onPlanChange() {
    this.changeValorizacion();
  }

  addValorization(valorization: Valorization): void {
    this.isAdmission =
      valorization.ValorizationTypeId === <number>ValorizationType.Adminission;
    this.valorizationFormGroup.patchValue({
      RightsBonus: valorization.RightsBonus,
      FeeBonus: valorization.FeeBonus,
      SupplieBonus: valorization.SupplieBonus,
      ModuleBonus: valorization.ModuleBonus,
      Id: valorization.Id,
      AssuranceAmount: valorization.AssuranceAmount,
      ValorizationTypeId: valorization.ValorizationTypeId,
      PlanId: valorization.PlanId,
      MedicalAppointmentId: valorization.MedicalAppointmentId,
      AliquotTax: valorization.AliquotTax,
      CreateDate: valorization.CreateDate,
      CreateUser: valorization.CreateUser,
      UpdateDate: valorization.UpdateDate,
      UpdateUser: valorization.UpdateUser,
    });

    if (this.isDisabled || this.isRegistered) {
      this.valorizationFormGroup.get("AliquotTax").disable();
    }
  }

  addValorizationItemsAmount(
    valorizationItemsAmount: ValorizationItemAmount[],
  ): void {
    this.form.controls.ValorizationItemsAmount = this.formBuilder.array([]);
    this.valorizationItemsDataSource.next(
      this.valorizationItemsAmount.controls,
    );

    valorizationItemsAmount.forEach((item) => {
      const row = this.formBuilder.group({
        RightsAmount: [item.RightsAmount],
        FeeAmount: [item.FeeAmount],
        ModuleAmount: [item.ModuleAmount],
        SupplieAmount: [item.SupplieAmount],
        RightsCoverage: [item.RightsCoverage],
        FeeCoverage: [item.FeeCoverage],
        SupplieCoverage: [item.SupplieCoverage],
        ModuleCoverage: [item.ModuleCoverage],
        RightsEntityAmount: [item.RightsEntityAmount],
        FeeEntityAmount: [item.FeeEntityAmount],
        ModuleEntityAmount: [item.ModuleEntityAmount],
        SupplieEntityAmount: [item.SupplieEntityAmount],
        PatientAmount: [item.PatientAmount],
        EntityAmount: [item.EntityAmount],
        TotalAmount: [item.TotalAmount],
        PatientTaxAmount: [item.PatientTaxAmount],
        EntityTaxAmount: [item.EntityTaxAmount],
        TotalTaxAmount: [item.TotalTaxAmount],
        ValorizationItem: [item.ValorizationItem],
        ValorizationItemSupplies: this.getValorizationItemSuppliesFormArray(
          item.ValorizationItemSupplies,
        ),
      });

      this.valorizationItemsAmount.push(row);
    });

    this.valorizationItemsDataSource.next(
      this.valorizationItemsAmount.controls,
    );
    this.changeValorizacion();
  }

  getValorizationItemSuppliesFormArray(
    valorizationItemSupplies: ValorizationItemSupplie[],
  ): FormArray {
    const array = this.formBuilder.array([]);
    if (valorizationItemSupplies && valorizationItemSupplies.length > 0) {
      valorizationItemSupplies.forEach((item) => {
        const row = this.formBuilder.group({
          Id: [item.Id],
          Amount: [item.Amount],
          EntityAmount: [item.EntityAmount],
          PatientAmount: [item.PatientAmount],
          SupplieName: [item.SupplieName],
          SupplieId: [item.SupplieId],
          SupplieTypeId: [item.SupplieTypeId],
          ValorizationItemId: [item.ValorizationItemId],
        });
        array.push(row);
      });
    }
    return array;
  }

  displayPracticeName(index: number): string {
    const medicalAppointmentProcedure = (<ValorizationItemAmount>(
      this.valorizationItemsAmount.controls[index].value
    )).ValorizationItem.MedicalAppointmentProcedure;

    if (medicalAppointmentProcedure.SetSupplieName) {
      return medicalAppointmentProcedure.SetSupplieName;
    } else if (medicalAppointmentProcedure.PracticeRegionAditionalName) {
      return (
        medicalAppointmentProcedure.PracticeRegionAditionalName +
        this.getPracticeName(medicalAppointmentProcedure)
      );
    } else {
      return (
        medicalAppointmentProcedure.PracticeRegionName +
        this.getPracticeName(medicalAppointmentProcedure)
      );
    }
  }

  private getPracticeName(
    medicalAppointmentProcedure: MedicalAppointmentProcedure,
  ): string {
    const practiceId = medicalAppointmentProcedure.PracticeId;

    if (
      medicalAppointmentProcedure.SetSupplieId ||
      !practiceId ||
      this.isAdmission
    ) {
      return "";
    } else {
      const practiceIndex = this.practices.findIndex(
        (p) => p.Id === practiceId,
      );
      if (practiceIndex > -1) {
        return " - " + this.practices[practiceIndex].Name;
      } else {
        return "";
      }
    }
  }

  displayQuantity(index: number): number {
    return (<ValorizationItemAmount>(
      this.valorizationItemsAmount.controls[index].value
    )).ValorizationItem.MedicalAppointmentProcedure.Quantity;
  }

  displayFeeAmount(index: number): string {
    return this.toFixed(
      (<ValorizationItemAmount>(
        this.valorizationItemsAmount.controls[index].value
      )).FeeAmount,
    );
  }

  displayFeeCoverage(index: number): string {
    return this.toFixed(
      (<ValorizationItemAmount>(
        this.valorizationItemsAmount.controls[index].value
      )).FeeCoverage,
    );
  }

  displayRightsAmount(index: number): string {
    return this.toFixed(
      (<ValorizationItemAmount>(
        this.valorizationItemsAmount.controls[index].value
      )).RightsAmount,
    );
  }

  displayRightsCoverage(index: number): string {
    return this.toFixed(
      (<ValorizationItemAmount>(
        this.valorizationItemsAmount.controls[index].value
      )).RightsCoverage,
    );
  }

  displayModuleAmount(index: number): string {
    return this.toFixed(
      (<ValorizationItemAmount>(
        this.valorizationItemsAmount.controls[index].value
      )).ModuleAmount,
    );
  }

  displayModuleCoverage(index: number): string {
    return this.toFixed(
      (<ValorizationItemAmount>(
        this.valorizationItemsAmount.controls[index].value
      )).ModuleCoverage,
    );
  }

  displaySupplieAmount(index: number): string {
    return this.toFixed(
      (<ValorizationItemAmount>(
        this.valorizationItemsAmount.controls[index].value
      )).SupplieAmount,
    );
  }

  displaySupplieCoverage(index: number): string {
    return this.toFixed(
      (<ValorizationItemAmount>(
        this.valorizationItemsAmount.controls[index].value
      )).SupplieCoverage,
    );
  }

  displayAssuranceAmount(index: number): string {
    return this.toFixed(
      (<ValorizationItemAmount>(
        this.valorizationItemsAmount.controls[index].value
      )).ValorizationItem.AssuranceAmount,
    );
  }

  displayTotalAmount(index: number): string {
    const valorizationItemAmount = <ValorizationItemAmount>(
      this.valorizationItemsAmount.controls[index].value
    );
    return this.toFixed(
      valorizationItemAmount.ValorizationItem.MedicalAppointmentProcedure
        .Quantity * valorizationItemAmount.TotalAmount,
    );
  }

  displayPatientAmountWithTax(index: number): string {
    const valorizationItemAmount = <ValorizationItemAmount>(
      this.valorizationItemsAmount.controls[index].value
    );
    return this.toFixed(
      valorizationItemAmount.ValorizationItem.MedicalAppointmentProcedure
        .Quantity *
        (valorizationItemAmount.PatientAmount +
          valorizationItemAmount.PatientTaxAmount),
    );
  }

  displayEntityAmountWithTax(index: number): string {
    const valorizationItemAmount = <ValorizationItemAmount>(
      this.valorizationItemsAmount.controls[index].value
    );
    return this.toFixed(
      valorizationItemAmount.ValorizationItem.MedicalAppointmentProcedure
        .Quantity *
        (valorizationItemAmount.EntityAmount +
          valorizationItemAmount.EntityTaxAmount),
    );
  }

  async aliquotTaxSelected(aliquotTax: AliquotTax) {
    this.valorizationFormGroup.patchValue({ AliquotTax: aliquotTax });
    if (aliquotTax) {
      let valorizationAmount = <ValorizationAmount>this.form.getRawValue();
      valorizationAmount =
        this.valorizationService.recalculatePatientTaxAmount(
          valorizationAmount,
        );
      this.addValorizationItemsAmount(
        valorizationAmount.ValorizationItemsAmount,
      );
    }
    this.changeValorizacion();
  }

  async entitySelected(entity: Entity) {
    this.planSelected();

    if (entity) {
      const plans = await this.filterService.getPlanFilter(entity.Id);
      this.filterCompletePlans = new FilterCompleteService<Plan>(
        plans,
        this.form.controls.Plan,
      );
      if (plans.length === 1) {
        await this.planSelected();
      }
    }

    this.changeValorizacion();
  }

  async planSelected() {
    this.changeValorizacion();
  }

  toFixed(number): string {
    if (number) {
      return number.toFixed(2);
    } else {
      return "";
    }
  }

  loadPosiblePractices() {
    if (
      this.valorizationFormGroup.controls.PlanId.value &&
      this.valorizationFormGroup.controls.PlanId.value > 0
    ) {
      this.valorizationService
        .getPracticesRegionSearchReservationToAdd(this.form.getRawValue())
        .then(
          (practices) => {
            this.posiblePractices = practices;
          },
          (err) => {
            this.notificationToastService.error(
              JSON.stringify(err.error),
              "Valorización",
            );
          },
        );
    }
  }

  async recalculate() {
    if (this.form.controls.Plan.value) {
      await this.loadAliquotsTax();
      this.onRecalculateClick.emit();
    }
  }

  async loadAliquotsTax() {
    const plan = <Plan>this.form.getRawValue().Plan;

    this.aliquotsTax = await this.filterService.getAliquotsTaxByPlanId(plan.Id);

    this.filterCompleteAliquotsTax = new FilterCompleteService<AliquotTax>(
      this.aliquotsTax,
      this.form.controls.AliquotTax,
    );
    this.setearAlicuota();
  }

  edit(index: number): void {
    const item = <ValorizationItemAmount>(
      this.valorizationItemsAmount.controls[index].value
    );

    const dialogRef = this.dialog.open(
      ValorizationItemAmountEditDialogComponent,
      {
        width: "50%",
        data: {
          item,
          noUpdateLikeParticular: false,
        },
      },
    );

    dialogRef.afterClosed().subscribe((value: any) => {
      if (value) {
        if (value.toParticular) {
          this.onRefreshPracticeToParticular.emit(value.item);
        } else {
          this.onRefreshPractice.emit(value.item);
        }
      }
    });
  }

  addSetInsumo(index: number): void {
    if (!this.form.getRawValue().Plan) {
      return;
    }

    const item = <ValorizationItemAmount>(
      this.valorizationItemsAmount.controls[index].value
    );

    const setSupplieIds = this.valorizationItemsAmount.controls
      .filter((item) => {
        return item.value.ValorizationItem.MedicalAppointmentProcedure
          .SetSupplieId;
      })
      .map((item) => {
        return item.value.ValorizationItem.MedicalAppointmentProcedure
          .SetSupplieId;
      });

    const dialogRef = this.dialog.open(
      ValorizationItemAddSetInsumoDialogComponent,
      {
        width: "50%",
        data: {
          item,
          existingSetSupplieIds: setSupplieIds,
          planId: this.form.getRawValue().Plan.Id,
        },
      },
    );

    dialogRef.afterClosed().subscribe((agreementPriceIds: any) => {
      if (agreementPriceIds) {
        this.onAddSetInsumoClick.emit({
          item,
          agreementPriceIds,
        });
      }
    });
  }

  add(): void {
    if (
      this.form.controls.PracticeRegionAdd.value &&
      this.form.controls.PracticeRegionQuantityAdd.value
    ) {
      this.form.controls.PracticeRegionAdd.value.Quantity =
        this.form.controls.PracticeRegionQuantityAdd.value;
      this.onAddPracticeClick.emit(this.form.controls.PracticeRegionAdd.value);
    } else if (!this.form.controls.PracticeRegionQuantityAdd.value) {
      this.notificationToastService.error(
        "Ingrese la cantidad",
        "Valorización",
      );
    }
  }

  onPracticeRegionAddChange(): void {
    this.form.patchValue({ PracticeRegionQuantityAdd: "" });
  }

  apacticeRegionAddSelected(practice: PracticeRegionSearchReservation): void {
    if (practice) {
      this.form.patchValue({ PracticeRegionQuantityAdd: practice.Quantity });
    }
  }

  canAddSetInsumo(index: number): boolean {
    const item = <ValorizationItemAmount>(
      this.valorizationItemsAmount.controls[index].value
    );

    if (this.isDisabled) {
      return false;
    } else {
      return !item.ValorizationItem.MedicalAppointmentProcedure.SetSupplieId;
    }
  }

  canEdit(index: number): boolean {
    const item = <ValorizationItemAmount>(
      this.valorizationItemsAmount.controls[index].value
    );

    const agreementPriceId = item.ValorizationItem.AgreementPriceId;
    if (this.isRegistered) {
      return false;
    } else if (this.isDisabled) {
      return false;
    } else {
      return (
        !this.isAdmission ||
        this.editableAgreementPriceIds.findIndex(
          (a) => a === agreementPriceId,
        ) > -1
      );
    }
  }

  patientAmount() {
    let total = 0;
    for (
      let index = 0;
      index < this.valorizationItemsAmount.controls.length;
      index++
    ) {
      const valorizationItemAmount = <ValorizationItemAmount>(
        this.valorizationItemsAmount.controls[index].value
      );
      total +=
        valorizationItemAmount.ValorizationItem.MedicalAppointmentProcedure
          .Quantity *
        (valorizationItemAmount.PatientAmount +
          valorizationItemAmount.PatientTaxAmount);
    }

    return this.toFixed(total);
  }

  async ngOnInit() {
    this.practices = await this.cacheFilterService.getPracticesFilters();
    this.filterCompleteEntities = new FilterCompleteService<Entity>(
      await this.filterService.getEntitiesFilter(),
      this.form.controls.Entity,
    );
  }

  setearAlicuota(): void {
    let aliquotTax: AliquotTax;

    if (this.aliquotsTax) {
      if (this.aliquotsTax.length === 1) {
        aliquotTax = this.aliquotsTax[0];
      } else if (this.aliquotsTax.length > 0) {
        if (this.valorizationFormGroup.controls.AliquotTax.value) {
          aliquotTax = this.aliquotsTax.find(
            (a) =>
              a.Id === this.valorizationFormGroup.controls.AliquotTax.value.Id,
          );
        }
      }

      this.valorizationFormGroup.patchValue({ AliquotTax: aliquotTax });
      this.changeValorizacion();
    }
  }
}
